import { useAppDisplayModeContext } from '@archipro-design/aria';
import type { ProjectPageLoader } from '@modules/project/type';
import { Image } from '@rocco/components/image';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';
import { Link, useLoaderData } from 'react-router';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { Button } from '@rocco/components/button';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { useAtomValue, useSetAtom } from 'jotai';
import { uiContextAtomSocialSharingActions } from '@rocco/ui/social-sharing/components/SocialSharingActions';
import { ReactComponent as ShareIcon } from '@rocco/icons/svg/share.svg';
import { useTracker } from '@archipro-website/tracker';
import { fireProfessionalLinkEvent } from '../../util/tracker-helpers';
import { useNavigate } from 'react-router';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    aboutProfessionalLogoElementAtom,
    footerElementAtom,
} from '~/state/project-detail-states';
import { decodeEntities } from '~/utils/stringHelper';
import { ProfessionalInfo } from './ProfessionalInfo';
import { CustomArrowRightThick } from '@archipro-design/icons';

/**
 * Professional data interface for the AboutProfessional component
 */
interface AboutProfessionalData {
    Professional: ProjectPageLoader['project']['Professional'];
}

/**
 * Props for the AboutProfessional component
 */
interface AboutProfessionalProps {
    /** Professional data containing company information and capabilities */
    data: AboutProfessionalData;
    /** Optional array of tile data for related content grid */
    tiles?: TileViewModel<TileableModel>[];
    showEnquiryCTA?: boolean;
    showSpecifierLoginPrompt?: boolean;
}

/**
 * Interface for navigation link configuration
 */
interface NavigationLink {
    label: string;
    link: string;
}

/**
 * Constants for component styling and layout
 */
const COMPONENT_CONSTANTS = {
    /** Maximum lines to display in the company summary before showing "Read more" */
    MAX_SUMMARY_LINES: 5,
    /** Grid display counts for related tiles */
    TILE_DISPLAY_COUNT: {
        desktop: 8,
        mobile: 4,
    },
    /** Image dimensions for company logo */
    LOGO_DIMENSIONS: {
        width: 136,
        height: 136,
    },
    /** Extra padding to ensure the grey background clears the footer gap */
    BACKGROUND_EXTRA_PADDING_PX: 100,
} as const;

/**
 * Generates navigation links for the professional's profile sections
 *
 * @param professional - Professional data object containing capabilities and URL segment
 * @param isMobile - Whether the display is mobile (affects link labels)
 * @returns Array of navigation link objects
 */
const generateNavigationLinks = (
    professional: ProjectPageLoader['project']['Professional'],
    isMobile: boolean,
): NavigationLink[] => {
    const baseUrl = `/professional/${professional.URLSegment}`;

    return [
        // Always show profile home link
        {
            label: isMobile ? 'Home' : 'View Profile',
            link: baseUrl,
        },
        // Always show about section
        {
            label: 'About',
            link: `${baseUrl}/info#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        // Conditionally show products section
        ...(professional.HasProducts
            ? [
                  {
                      label: 'Products',
                      link: `${baseUrl}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        // Always show projects section
        {
            label: 'Projects',
            link: `${baseUrl}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        // Conditionally show articles section
        ...(professional.HasArticles
            ? [
                  {
                      label: 'Articles',
                      link: `${baseUrl}/journal#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        // Conditionally show videos section
        ...(professional.HasVideos
            ? [
                  {
                      label: 'Videos',
                      link: `${baseUrl}/videos#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        // Conditionally show network/partners section
        ...(professional.HasPartners
            ? [
                  {
                      label: 'Network',
                      link: `${baseUrl}/partners#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        // Always show contact section
        {
            label: 'Contact',
            link: `${baseUrl}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
    ];
};

/**
 * AboutProfessional Component
 *
 * Displays comprehensive information about a professional on a project page.
 * This includes the company logo, summary, navigation links to different profile sections,
 * social sharing functionality, and related projects grid.
 *
 * Features:
 * - Company logo with decorative styling
 * - Expandable company summary with "Read more" functionality
 * - Dynamic navigation based on professional's capabilities
 * - Social sharing integration
 * - Website link (if available)
 * - Related projects/content grid
 * - Desktop-only display (returns null on mobile)
 *
 * @param props - Component props
 * @returns JSX element or null if on mobile
 */
const AboutProfessional = ({
    data,
    tiles = [],
    showEnquiryCTA = true,
    showSpecifierLoginPrompt = false,
}: AboutProfessionalProps) => {
    const { desktop } = useAppDisplayModeContext();
    const { Professional } = data;

    const { project, featuredProjects } = useLoaderData<typeof loader>();

    const tracker = useTracker();
    // Set up tracking for analytics
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: Professional.ID,
        professionalTitle: Professional.Title,
        itemType: 'Project',
    });
    const readMoreUrl = `/professional/${Professional.URLSegment}/info#${PROFESSIONAL_SECTIONS_ANCHOR}`;
    const navigate = useNavigate();

    // Generate navigation links based on professional capabilities
    const navigationLinks = generateNavigationLinks(Professional, !desktop);

    // Get social sharing actions from global state
    const socialSharingActions = useAtomValue(
        uiContextAtomSocialSharingActions,
    );

    /**
     * Handles the share button click by showing the social sharing modal
     */
    const handleShareClick = () => {
        if (!socialSharingActions) return;
        socialSharingActions.showSocialSharingModal();
    };

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(Professional.Website, {
            libraryItemID: Professional.ID,
            professionalID: Professional.ID,
            professionalTitle: Professional.Title,
        });

    const setAboutProfessionalLogoElement = useSetAtom(
        aboutProfessionalLogoElementAtom,
    );
    const footerElement = useAtomValue(footerElementAtom);

    const logoRef = useRef<HTMLDivElement>(null);
    const bannerRef = useRef<HTMLDivElement>(null);
    const [backgroundHeight, setBackgroundHeight] = useState('200vh');

    /** Calculates and sets the grey background height from banner midpoint to footer top */
    const updateBackgroundHeight = useCallback(() => {
        if (!bannerRef.current || !footerElement) return;

        const bannerRect = bannerRef.current.getBoundingClientRect();
        const footerRect = footerElement.getBoundingClientRect();

        const bannerMiddle =
            bannerRect.top + window.scrollY + bannerRect.height / 2;
        const footerTop = footerRect.top + window.scrollY;

        const heightNeeded = Math.max(
            footerTop -
                bannerMiddle +
                COMPONENT_CONSTANTS.BACKGROUND_EXTRA_PADDING_PX,
            window.innerHeight,
        );

        setBackgroundHeight(`${Math.ceil(heightNeeded)}px`);
    }, [footerElement]);

    useEffect(() => {
        // Set the element reference directly instead of calculating offset
        if (logoRef.current) {
            setAboutProfessionalLogoElement(logoRef.current);
        }

        // Clean up by setting to null when component unmounts or changes
        return () => {
            setAboutProfessionalLogoElement(null);
        };
    }, [setAboutProfessionalLogoElement, Professional.ID, project.ID]);

    // Calculate background height based on footer position
    useEffect(() => {
        if (!footerElement || !bannerRef.current) return;

        updateBackgroundHeight();

        const handleResize = () => {
            updateBackgroundHeight();
        };

        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => updateBackgroundHeight())
                : null;

        const contentElement = document.getElementById('app-page-content');

        resizeObserver?.observe(bannerRef.current);
        resizeObserver?.observe(footerElement);
        if (contentElement) {
            resizeObserver?.observe(contentElement);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
        };
    }, [footerElement, updateBackgroundHeight]);

    return (
        <>
            {/* Header section with logo and decorative line */}
            <div className="relative mb-45 h-60">
                <div className="absolute top-25.5 h-[1px] w-full bg-[#989898]"></div>

                {Professional.CompanyLogo && (
                    <div
                        ref={logoRef}
                        className={
                            'border-1 absolute left-54 top-0 h-51.5 w-51.5 overflow-hidden rounded-full border border-[#989898] bg-white'
                        }
                        style={{
                            backgroundColor:
                                Professional.LogoBackgroundColor || 'white',
                        }}
                    >
                        <Image
                            className="absolute left-1/2 top-1/2 w-3/4 -translate-x-1/2 -translate-y-1/2"
                            src={Professional.CompanyLogo}
                            alt={Professional.Title}
                            width={COMPONENT_CONSTANTS.LOGO_DIMENSIONS.width}
                            height={COMPONENT_CONSTANTS.LOGO_DIMENSIONS.height}
                            quality={100}
                            priority
                        />
                    </div>
                )}

                {/* Professional Info Section - positioned to the right of logo and below decorative line */}
                <div className="absolute left-122 right-0 top-31.5">
                    <ProfessionalInfo
                        data={{ Professional }}
                        projectId={project.ID}
                        showEnquiryCTA={showEnquiryCTA}
                        showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                    />
                </div>
            </div>
            {/* Content section with summary and title */}
            <div
                className="line-clamp-4 flex cursor-pointer items-center font-display text-20 font-[600] leading-[1.08] tracking-[-0.08rem] text-[#D2D2D2]"
                style={{
                    wordBreak: 'keep-all',
                    overflowWrap: 'normal',
                    hyphens: 'none',
                }}
                onClick={(e) => {
                    fireProfessionalLinkEvent(
                        'ReadMore',
                        window.location.href,
                        tracker,
                    );
                    if (e) {
                        clickProxy(e);
                    }

                    navigate(readMoreUrl);
                }}
            >
                <span className="inline text-[#161616]">
                    {Professional.Title && Professional.Title + '. '}
                </span>
                <span className="inline">
                    {decodeEntities(
                        Professional.BlockSummary?.replace(/<[^>]*>?/gm, '') ||
                            '',
                    )}
                </span>
            </div>
            {/* Professional Metrics Section */}
            <div className="my-40 flex">
                {/* Column 1: Year Founded / Year Joined */}
                <div className="flex flex-1 flex-col pr-8 font-medium text-black">
                    <h3 className="mb-12 text-7">
                        {Professional.YearFounded ? 'Founded' : 'Year Joined'}
                    </h3>
                    <div className="mb-7 text-55 leading-[1] tracking-[-0.5rem] text-[#171616]">
                        {Professional.YearFounded ||
                            Professional.ProfileLiveDate?.substring(0, 4)}
                    </div>
                    <div className="text-6 font-normal leading-[130%] tracking-[-0.03rem]">
                        {Professional.YearFounded
                            ? 'Established presence in the industry.'
                            : 'Established presence on ArchiPro.'}
                    </div>
                </div>

                {/* Column 2: Projects Listed */}
                <div className="flex flex-1 flex-col border-l border-black px-8 font-medium">
                    <h3 className="mb-12 text-7">Projects Listed</h3>
                    <div className="mb-7 text-55 leading-[1] tracking-[-0.5rem] text-[#171616]">
                        {Professional.ProjectCount || 0}
                    </div>
                    <div className="text-6 font-normal leading-[130%] tracking-[-0.03rem]">
                        A portfolio of work to explore.
                    </div>
                </div>

                {/* Column 3: Responds within (conditional) */}
                {Professional.EnquiryAvgResponseHours &&
                Professional.EnquiryAvgResponseHours > 0 &&
                Professional.EnquiryAvgResponseHours <= 48 ? (
                    <div className="flex flex-1 flex-col border-l border-black pl-8 font-medium text-black">
                        <h3 className="mb-12 text-7">Responds within</h3>
                        <div className="mb-7 text-55 leading-[1] tracking-[-0.5rem] text-[#171616]">
                            {Professional.EnquiryAvgResponseHours < 24
                                ? `${Professional.EnquiryAvgResponseHours}hr`
                                : `${Math.round(Professional.EnquiryAvgResponseHours / 24)}d`}
                        </div>
                        <div className="text-6 font-normal leading-[130%] tracking-[-0.03rem]">
                            Typically replies within the stated time.
                        </div>
                    </div>
                ) : null}
            </div>
            {/* Professional's grayscale banner image */}
            {Professional.BannerImages &&
                Professional.BannerImages.length > 0 && (
                    <div className="relative" ref={bannerRef}>
                        {/* Full-width gray background starting from halfway down the banner */}
                        <div
                            className="absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 bg-[#DCDCDC]"
                            style={{
                                height: backgroundHeight,
                                // Full visible viewport width: parent content (100%) + section padding (6rem)
                                // Adding 6rem (md:px-12 = 3rem * 2) extends to viewport edges
                                width: 'calc(100% + 6rem)',
                            }}
                        ></div>

                        <div className="relative aspect-[1820/968] h-auto w-full overflow-hidden">
                            {/* Banner Image with Grayscale */}
                            <Image
                                src={
                                    Professional.BannerImages[0]
                                        ?.LibraryImageFile?.DetailBannerStandard
                                        ?.Desktop || ''
                                }
                                alt={`${Professional.Title} banner`}
                                className="h-full w-full object-cover grayscale"
                                onLoad={updateBackgroundHeight}
                            />

                            {/* Dark Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-40"></div>

                            {/* Professional Name - Top Left */}
                            <div className="absolute left-10 top-10 w-full">
                                <h2 className="font-display text-28 font-semibold leading-[1] text-white">
                                    {Professional.Title + '.'}
                                </h2>
                            </div>

                            {/* Navigation Links - Bottom Right */}
                            <div className="absolute bottom-10 right-10 flex flex-col font-display font-semibold leading-[1]">
                                {/* Profile Link */}
                                <div
                                    className="flex cursor-pointer items-center gap-2 text-white transition-all hover:text-gray-200"
                                    onClick={(e) => {
                                        fireProfessionalLinkEvent(
                                            'Profile',
                                            window.location.href,
                                            tracker,
                                        );
                                        if (e) {
                                            clickProxy(e);
                                        }
                                        navigate(
                                            `/professional/${Professional.URLSegment}`,
                                        );
                                    }}
                                >
                                    <span className="text-20">Profile</span>
                                    <CustomArrowRightThick className="mt-2 h-20 w-20 -rotate-45 fill-[#FAFAFA]" />
                                </div>

                                {/* Projects Link */}
                                <div
                                    className="flex cursor-pointer items-center gap-2 text-white transition-all hover:text-gray-200"
                                    onClick={(e) => {
                                        fireProfessionalLinkEvent(
                                            'Projects',
                                            window.location.href,
                                            tracker,
                                        );
                                        if (e) {
                                            clickProxy(e);
                                        }
                                        navigate(
                                            `/professional/${Professional.URLSegment}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                        );
                                    }}
                                >
                                    <span className="text-20">Projects</span>
                                    <CustomArrowRightThick className="mt-2 h-20 w-20 -rotate-45 fill-[#FAFAFA]" />
                                </div>

                                {/* Contact Link */}
                                <div
                                    className="flex cursor-pointer items-center gap-2 text-white transition-all hover:text-gray-200"
                                    onClick={(e) => {
                                        fireProfessionalLinkEvent(
                                            'Contact',
                                            window.location.href,
                                            tracker,
                                        );
                                        if (e) {
                                            clickProxy(e);
                                        }
                                        navigate(
                                            `/professional/${Professional.URLSegment}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                        );
                                    }}
                                >
                                    <span className="text-20">Contact</span>
                                    <CustomArrowRightThick className="mt-2 h-20 w-20 -rotate-45 fill-[#FAFAFA]" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            {/* Navigation and actions section */}
            <div>
                <nav className="hidden justify-between gap-2 pb-8 pt-18">
                    {/* Navigation links */}
                    <div className="flex gap-2">
                        {navigationLinks.map((navLink, index) => (
                            <Button
                                key={navLink.label}
                                className="flex items-center bg-[#1414140d] first:bg-gray-900"
                                color={
                                    index === 0
                                        ? 'dark'
                                        : 'header-search-button'
                                }
                                onClick={() => {
                                    fireProfessionalLinkEvent(
                                        navLink.label,
                                        window.location.href,
                                        tracker,
                                    );
                                }}
                            >
                                <Link
                                    to={navLink.link}
                                    prefetch="intent"
                                    preventScrollReset
                                >
                                    {navLink.label}
                                </Link>
                            </Button>
                        ))}
                    </div>

                    {/* Action buttons (share and website) */}
                    <div className="flex gap-2 text-[#141414]">
                        {/* Share button */}
                        <Button
                            color="header-search-button"
                            className="flex items-center bg-[#1414140d]"
                            onClick={() => {
                                fireProfessionalLinkEvent(
                                    'Share Pro',
                                    window.location.href,
                                    tracker,
                                );
                                handleShareClick();
                            }}
                        >
                            <ShareIcon className="size-4" />
                            <span className="mx-2">Share Pro</span>
                        </Button>

                        {/* Website link (conditional) */}
                        {Professional.Website && referralTrackingURL && (
                            <Button
                                color="header-search-button"
                                className="flex items-center bg-[#1414140d]"
                                asChild
                            >
                                <a
                                    href={referralTrackingURL}
                                    target="_blank"
                                    {...referralTrackingParams}
                                    rel="noopener noreferrer"
                                >
                                    Visit Website
                                </a>
                            </Button>
                        )}
                    </div>
                </nav>

                {/* Related content grid */}
                <div className="mt-18">
                    <div className="mb-8 font-display text-9 font-semibold leading-[100%] tracking-[-0.4px]">
                        Project Portfolio
                    </div>
                    <DetailsPageRelatedTileGrid
                        type="projects"
                        title=""
                        appDisplayMode={desktop ? 'desktop' : 'mobile'}
                        displayCount={COMPONENT_CONSTANTS.TILE_DISPLAY_COUNT}
                        tiles={tiles}
                        section="ProjectAboutProfessionalGrid"
                        cta={
                            featuredProjects.FeaturedProjects.pageInfo
                                .totalCount > 8
                                ? {
                                      label: 'View all Projects',
                                      onClick: () => {
                                          fireProfessionalLinkEvent(
                                              'View all Projects',
                                              window.location.href,
                                              tracker,
                                          );
                                      },
                                      type: 'link',
                                      color: 'primary',
                                      to: `/professional/${Professional.URLSegment}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                  }
                                : undefined
                        }
                    />
                </div>
            </div>
        </>
    );
};

export default AboutProfessional;
